import { Link, useParams, Navigate } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { getRegion, regions } from "@/data/regions";
import { INTERNATIONAL_CITIES } from "@/data/internationalCities";
import { UK_CATEGORIES } from "@/data/ukCategories";
import { NL_CATEGORIES } from "@/data/nlCategories";
import { DE_CATEGORIES } from "@/data/deCategories";
import { FR_CATEGORIES } from "@/data/frCategories";
import { SE_CATEGORIES } from "@/data/seCategories";
import { FI_CATEGORIES } from "@/data/fiCategories";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

const SectionBlock = ({ section }) => (
  <Reveal as="section" style={{ marginTop: "2.6rem" }}>
    <h2
      className="display"
      style={{
        fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
        margin: "0 0 1.1rem",
        letterSpacing: "-.01em",
        lineHeight: 1.15,
      }}
    >
      {section.h2}
    </h2>
    {(section.body || []).map((para, i) => (
      <p
        key={i}
        style={{
          color: "var(--ink-soft)",
          fontSize: "1.04rem",
          lineHeight: 1.75,
          margin: "0 0 1.1rem",
          maxWidth: "68ch",
        }}
      >
        {para}
      </p>
    ))}
    {section.bullets && (
      <ul className="cap-list" style={{ marginTop: ".6rem", maxWidth: "70ch" }}>
        {section.bullets.map((b, i) => (
          <li key={i}>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    )}
  </Reveal>
);

export const RegionDetail = () => {
  const { id } = useParams();
  const data = getRegion(id);

  // Reciprocal hreflang cluster only for regions that have a localized
  // equivalent (Phase 2 Germany, Phase 4 France). Cluster is strictly
  // 2-way (EN + local) — no cross-cluster German↔French linking.
  const HREFLANG_MAP = {
    germany: {
      en: "/regions/germany",
      de: "/de/beschaffung-indien",
      xDefault: "/regions/germany",
      localLang: "de",
    },
    france: {
      en: "/regions/france",
      fr: "/fr/approvisionnement-inde",
      xDefault: "/regions/france",
      localLang: "fr",
    },
  };
  const hreflang = data ? HREFLANG_MAP[data.id] : null;
  const alternates = hreflang
    ? [
        { hreflang: "en", href: `${SITE_URL}${hreflang.en}` },
        { hreflang: hreflang.localLang, href: `${SITE_URL}${hreflang[hreflang.localLang]}` },
        { hreflang: "x-default", href: `${SITE_URL}${hreflang.xDefault}` },
      ]
    : null;

  useSeo({
    title: data ? data.title : "Regional sourcing guide",
    description: data ? data.metaDescription : undefined,
    path: data ? `/regions/${data.id}` : "/regions",
    type: "article",
    alternates,
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${SITE_URL}/regions/${data.id}#service`,
              name: `India sourcing for ${data.country} buyers`,
              description: data.metaDescription,
              provider: { "@id": `${SITE_URL}#organization` },
              areaServed: { "@type": "Country", name: data.country },
              serviceType: "Buying agent · Sourcing · Quality inspection",
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}/regions/${data.id}#faq`,
              mainEntity: (data.faqs || []).map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "Regions", path: "/regions" },
              { name: data.country, path: `/regions/${data.id}` },
            ]),
          ],
        }
      : null,
    jsonLdId: "region-detail-jsonld",
  });

  if (!data) return <Navigate to="/regions" replace />;

  const idx = regions.findIndex((r) => r.id === id);
  const prev = regions[(idx - 1 + regions.length) % regions.length];
  const next = regions[(idx + 1) % regions.length];

  return (
    <article data-testid="region-detail-page">
      {/* HERO */}
      <header
        className="wrap"
        style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}
      >
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }}>Home</Link> ·{" "}
            <Link to="/regions" style={{ color: "inherit" }}>Regions</Link> · {data.country}
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="region-eyebrow">
            <span aria-hidden="true" style={{ marginRight: ".5rem", fontSize: "1.4em", verticalAlign: "middle" }}>{data.flag}</span>
            {data.eyebrow}
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text={data.h1}
          className="display"
          stagger={70}
          testId="region-h1"
        />
        <Reveal as="p" delay={200} style={{ maxWidth: "72ch", color: "var(--ink-soft)", fontSize: "1.1rem", marginTop: "1.4rem", lineHeight: 1.65 }}>
          {data.intro}
        </Reveal>
      </header>

      {/* FACTS strip — ports of arrival */}
      <section
        className="wrap"
        style={{
          paddingBlock: "clamp(2rem, 4vw, 3.4rem)",
          borderBlock: "1px solid var(--line)",
        }}
        data-testid="region-facts"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          <Reveal>
            <span className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase" }}>
              Destination country
            </span>
            <p style={{ fontFamily: "var(--display)", fontWeight: 380, fontSize: "1.2rem", margin: ".3rem 0 0", letterSpacing: "-.005em" }}>
              {data.country}
            </p>
          </Reveal>
          <Reveal>
            <span className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase" }}>
              Discharge ports
            </span>
            <p style={{ fontFamily: "var(--display)", fontWeight: 380, fontSize: "1.05rem", margin: ".3rem 0 0", letterSpacing: "-.005em", lineHeight: 1.35 }}>
              {(data.ports || []).join(" · ")}
            </p>
          </Reveal>
          <Reveal>
            <span className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase" }}>
              Origin
            </span>
            <p style={{ fontFamily: "var(--display)", fontWeight: 380, fontSize: "1.2rem", margin: ".3rem 0 0", letterSpacing: "-.005em" }}>
              India (Mundra · Nhava Sheva)
            </p>
          </Reveal>
          <Reveal>
            <span className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase" }}>
              Service
            </span>
            <p style={{ fontFamily: "var(--display)", fontWeight: 380, fontSize: "1.2rem", margin: ".3rem 0 0", letterSpacing: "-.005em" }}>
              Buying agent · QC · Consolidation
            </p>
          </Reveal>
        </div>
      </section>

      {/* BODY sections */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}>
        {(data.sections || []).map((s, i) => (
          <SectionBlock key={i} section={s} />
        ))}
      </section>

      {/* CITY programmes — Phase 1 international city pages */}
      {(() => {
        const cities = INTERNATIONAL_CITIES.filter((c) => c.parentCountry === data.id);
        if (cities.length === 0) return null;
        return (
          <section
            className="wrap"
            data-testid="region-cities"
            style={{
              paddingBlock: "clamp(2.4rem, 5vw, 4rem)",
              borderTop: "1px solid var(--line)",
            }}
          >
            <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
              City programmes · {data.country}
            </Reveal>
            <Reveal
              as="h2"
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.4rem",
                letterSpacing: "-.01em",
              }}
            >
              India sourcing by {data.country} city
            </Reveal>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "1rem",
              }}
            >
              {cities.map((c, i) => (
                <Link
                  key={i}
                  to={c.slug}
                  data-testid={`region-city-card-${c.citySlug}`}
                  style={{
                    display: "block",
                    padding: "1.2rem 1.3rem",
                    border: "1px solid var(--line)",
                    color: "var(--ink)",
                    textDecoration: "none",
                    background: "var(--paper)",
                    transition: "border-color 200ms",
                  }}
                >
                  <p
                    className="mono"
                    style={{
                      color: "var(--brass-deep)",
                      fontSize: ".62rem",
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      margin: "0 0 .4rem",
                    }}
                  >
                    City programme · {c.city}
                  </p>
                  <p
                    style={{
                      fontFamily: "var(--display)",
                      fontSize: "1.05rem",
                      letterSpacing: "-.005em",
                      margin: 0,
                    }}
                  >
                    India sourcing agent · {c.city} →
                  </p>
                </Link>
              ))}
            </div>
          </section>
        );
      })()}

      {/* UK CATEGORY programmes — Phase 1 UK category silos (only on /regions/uk) */}
      {data.id === "uk" && (
        <section
          className="wrap"
          data-testid="region-uk-categories"
          style={{
            paddingBlock: "clamp(2.4rem, 5vw, 4rem)",
            borderTop: "1px solid var(--line)",
          }}
        >
          <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
            UK category programmes · seven silos
          </Reveal>
          <Reveal
            as="h2"
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
              margin: "0 0 1rem",
              letterSpacing: "-.01em",
            }}
          >
            India sourcing by UK category
          </Reveal>
          <Reveal
            as="p"
            style={{
              color: "var(--ink-soft)",
              fontSize: "1.02rem",
              lineHeight: 1.7,
              maxWidth: "68ch",
              margin: "0 0 1.6rem",
            }}
          >
            Deep-dive category playbooks for UK retailers, DTC brands, hospitality
            specifiers and importers — Indian manufacturing clusters, UK compliance
            stack (UKCA · UK-REACH · BS 5852 · BS EN 60598 · MCF Reg 1935/2004),
            typical MOQs, port lanes into Felixstowe and Southampton, and how each
            programme actually runs end-to-end.
          </Reveal>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "1rem",
            }}
          >
            {UK_CATEGORIES.map((c, i) => (
              <Link
                key={i}
                to={c.slug}
                data-testid={`region-uk-category-${c.categorySlug}`}
                style={{
                  display: "block",
                  padding: "1.3rem 1.4rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                  transition: "border-color 200ms, transform 200ms",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .5rem",
                  }}
                >
                  UK programme · {c.category}
                </p>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.08rem",
                    letterSpacing: "-.005em",
                    margin: "0 0 .6rem",
                    lineHeight: 1.35,
                  }}
                >
                  {c.category === "Buying agent"
                    ? "Independent India buying agent →"
                    : `${c.category} sourcing from India →`}
                </p>
                <p
                  style={{
                    color: "var(--ink-soft)",
                    fontSize: ".88rem",
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  {(c.keyFacts && c.keyFacts[0]) ? `${c.keyFacts[0][0]}: ${c.keyFacts[0][1]}` : ""}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* NL CATEGORY programmes — Phase 3 Netherlands category silos */}
      {data.id === "netherlands" && (
        <section
          className="wrap"
          data-testid="region-nl-categories"
          style={{
            paddingBlock: "clamp(2.4rem, 5vw, 4rem)",
            borderTop: "1px solid var(--line)",
          }}
        >
          <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
            Netherlands category programmes · four silos
          </Reveal>
          <Reveal
            as="h2"
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
              margin: "0 0 1rem",
              letterSpacing: "-.01em",
            }}
          >
            India sourcing by Netherlands category
          </Reveal>
          <Reveal
            as="p"
            style={{
              color: "var(--ink-soft)",
              fontSize: "1.02rem",
              lineHeight: 1.7,
              maxWidth: "68ch",
              margin: "0 0 1.6rem",
            }}
          >
            Category playbooks for Dutch retailers, DTC lifestyle brands and
            hospitality specifiers — Indian manufacturing clusters, EU compliance
            stack (CE · EU-REACH · EN 60598 · EN 12520 · GPSR), Rotterdam and
            Amsterdam port lanes, sustainability-certification density (GOTS ·
            GRS · OEKO-TEX · FSC), and how each programme runs end-to-end.
          </Reveal>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "1rem",
            }}
          >
            {NL_CATEGORIES.map((c, i) => (
              <Link
                key={i}
                to={c.slug}
                data-testid={`region-nl-category-${c.categorySlug}`}
                style={{
                  display: "block",
                  padding: "1.3rem 1.4rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                  transition: "border-color 200ms, transform 200ms",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .5rem",
                  }}
                >
                  Netherlands programme · {c.category}
                </p>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.08rem",
                    letterSpacing: "-.005em",
                    margin: "0 0 .6rem",
                    lineHeight: 1.35,
                  }}
                >
                  {c.category} sourcing from India →
                </p>
                <p
                  style={{
                    color: "var(--ink-soft)",
                    fontSize: ".88rem",
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  {(c.keyFacts && c.keyFacts[0]) ? `${c.keyFacts[0][0]}: ${c.keyFacts[0][1]}` : ""}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Sprint A · Global Furniture + Lighting SEO — Tier-1 EU country category silos.
          Adding a country here + its data export is all that's needed to onboard. */}
      {(() => {
        const COUNTRY_CATEGORY_MAP = {
          germany: { list: DE_CATEGORIES, label: "Germany", ports: "Hamburg and Bremerhaven", compliance: "GS-mark · DIN EN 1021 · DIN EN 12520 · DIN EN 1728 · EN 60598 · CE · REACH" },
          france: { list: FR_CATEGORIES, label: "France", ports: "Le Havre and Marseille-Fos", compliance: "NF D 60-013 · AGEC · EN 60598 · CE · REACH · Toubon Law" },
          sweden: { list: SE_CATEGORIES, label: "Sweden", ports: "Gothenburg (via Rotterdam feeder)", compliance: "EN 12520 · EN 1728 · EN 1021 · EN 60598 · CE · FSC · GOTS" },
          finland: { list: FI_CATEGORIES, label: "Finland", ports: "HaminaKotka and Helsinki", compliance: "EN 12520 · EN 1728 · EN 1021 · EN 60598 · CE · FSC · GOTS" },
        };
        const cfg = COUNTRY_CATEGORY_MAP[data.id];
        if (!cfg) return null;
        return (
          <section
            className="wrap"
            data-testid={`region-${data.id}-categories`}
            style={{
              paddingBlock: "clamp(2.4rem, 5vw, 4rem)",
              borderTop: "1px solid var(--line)",
            }}
          >
            <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
              {cfg.label} category programmes · furniture + lighting
            </Reveal>
            <Reveal
              as="h2"
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1rem",
                letterSpacing: "-.01em",
              }}
            >
              India sourcing by {cfg.label} category
            </Reveal>
            <Reveal
              as="p"
              style={{
                color: "var(--ink-soft)",
                fontSize: "1.02rem",
                lineHeight: 1.7,
                maxWidth: "68ch",
                margin: "0 0 1.6rem",
              }}
            >
              Deep-dive category playbooks for {cfg.label} retailers, DTC brands and
              hospitality specifiers — Indian manufacturing clusters, {cfg.label} compliance
              stack ({cfg.compliance}), typical MOQs, port lanes into {cfg.ports}, and how
              each programme actually runs end-to-end.
            </Reveal>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: "1rem",
              }}
            >
              {cfg.list.map((c, i) => (
                <Link
                  key={i}
                  to={c.slug}
                  data-testid={`region-${data.id}-category-${c.categorySlug}`}
                  style={{
                    display: "block",
                    padding: "1.3rem 1.4rem",
                    border: "1px solid var(--line)",
                    color: "var(--ink)",
                    textDecoration: "none",
                    background: "var(--paper)",
                    transition: "border-color 200ms, transform 200ms",
                  }}
                >
                  <p
                    className="mono"
                    style={{
                      color: "var(--brass-deep)",
                      fontSize: ".62rem",
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      margin: "0 0 .5rem",
                    }}
                  >
                    {cfg.label} programme · {c.category}
                  </p>
                  <p
                    style={{
                      fontFamily: "var(--display)",
                      fontSize: "1.08rem",
                      letterSpacing: "-.005em",
                      margin: "0 0 .6rem",
                      lineHeight: 1.35,
                    }}
                  >
                    {c.category} sourcing from India →
                  </p>
                  <p
                    style={{
                      color: "var(--ink-soft)",
                      fontSize: ".88rem",
                      lineHeight: 1.55,
                      margin: 0,
                    }}
                  >
                    {(c.keyFacts && c.keyFacts[0]) ? `${c.keyFacts[0][0]}: ${c.keyFacts[0][1]}` : ""}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Localized-version callout — for regions that have a native-language equivalent */}
      {(data.id === "germany" || data.id === "france") && (
        <section
          className="wrap"
          data-testid="region-localized-callout"
          style={{
            paddingBlock: "clamp(2rem, 4vw, 3rem)",
            borderTop: "1px solid var(--line)",
          }}
        >
          <Link
            to={data.id === "germany" ? "/de/beschaffung-indien" : "/fr/approvisionnement-inde"}
            style={{
              display: "block",
              padding: "1.6rem 1.8rem",
              border: "1px solid var(--brass-deep)",
              color: "var(--ink)",
              textDecoration: "none",
              background: "var(--paper)",
            }}
            hrefLang={data.id === "germany" ? "de" : "fr"}
            data-testid={`region-localized-link-${data.id === "germany" ? "de" : "fr"}`}
          >
            <p
              className="mono"
              style={{
                color: "var(--brass-deep)",
                fontSize: ".68rem",
                letterSpacing: ".14em",
                textTransform: "uppercase",
                margin: "0 0 .4rem",
              }}
            >
              {data.id === "germany" ? "Auf Deutsch lesen" : "Lire en français"}
            </p>
            <p
              style={{
                fontFamily: "var(--display)",
                fontSize: "1.2rem",
                letterSpacing: "-.005em",
                margin: 0,
              }}
            >
              {data.id === "germany"
                ? "Beschaffung aus Indien — deutschsprachiger Leitfaden →"
                : "Approvisionnement en Inde — guide en français →"}
            </p>
          </Link>
        </section>
      )}

      {/* Nordic Hub callout — only for Nordic country hubs */}
      {(data.id === "sweden" || data.id === "denmark" || data.id === "finland") && (
        <section
          className="wrap"
          data-testid="region-nordic-hub-callout"
          style={{
            paddingBlock: "clamp(2rem, 4vw, 3rem)",
            borderTop: "1px solid var(--line)",
          }}
        >
          <Link
            to="/regions/nordic-sustainable-sourcing"
            style={{
              display: "block",
              padding: "1.6rem 1.8rem",
              border: "1px solid var(--brass-deep)",
              color: "var(--ink)",
              textDecoration: "none",
              background: "var(--paper)",
            }}
          >
            <p
              className="mono"
              style={{
                color: "var(--brass-deep)",
                fontSize: ".68rem",
                letterSpacing: ".14em",
                textTransform: "uppercase",
                margin: "0 0 .4rem",
              }}
            >
              Cross-country hub · Nordics
            </p>
            <p
              style={{
                fontFamily: "var(--display)",
                fontSize: "1.2rem",
                letterSpacing: "-.005em",
                margin: 0,
              }}
            >
              Read: Nordic sustainable sourcing from India — FSC · GOTS · GRS · OEKO-TEX →
            </p>
          </Link>
        </section>
      )}

      {/* FAQ */}
      {data.faqs && data.faqs.length > 0 && (
        <section
          className="wrap"
          style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)", maxWidth: "820px" }}
          data-testid="region-faq"
        >
          <Reveal as="p" className="eyebrow">Frequently asked · {data.country}</Reveal>
          <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
            {data.country} buyer questions
          </Reveal>
          {data.faqs.map((f, i) => (
            <Reveal
              key={i}
              as="details"
              data-testid={`region-faq-${i}`}
              style={{ borderBottom: "1px solid var(--line)", padding: "1.4rem 0" }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontFamily: "var(--display)",
                  fontSize: "1.1rem",
                  color: "var(--ink)",
                  listStyle: "none",
                }}
              >
                {f.q}
              </summary>
              <p style={{ marginTop: ".8rem", color: "var(--ink-soft)", lineHeight: 1.7, maxWidth: "68ch" }}>
                {f.a}
              </p>
            </Reveal>
          ))}
        </section>
      )}

      {/* RELATED — cross-links to Services, Clusters, Products, Trends */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="region-related"
      >
        <Reveal as="p" className="eyebrow">Continue reading</Reveal>
        <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
          Ready to source from India?
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.2rem" }}>
          <Link to="/services/buying-agent-india" style={{ display: "block", padding: "1.2rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit" }} data-testid="region-related-service">
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>Service</p>
            <p style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".4rem 0 0" }}>Buying Agent India</p>
          </Link>
          <Link to="/services/quality-inspection-india" style={{ display: "block", padding: "1.2rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit" }}>
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>Service</p>
            <p style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".4rem 0 0" }}>Quality Inspection India</p>
          </Link>
          <Link to="/clusters" style={{ display: "block", padding: "1.2rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit" }} data-testid="region-related-clusters">
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>Origin</p>
            <p style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".4rem 0 0" }}>15 manufacturing clusters</p>
          </Link>
          <Link to="/products" style={{ display: "block", padding: "1.2rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit" }} data-testid="region-related-products">
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>Catalogue</p>
            <p style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".4rem 0 0" }}>Product categories</p>
          </Link>
          <Link to="/trends/india-vs-china-sourcing-comparison" style={{ display: "block", padding: "1.2rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit" }} data-testid="region-related-trends">
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>Trends & News</p>
            <p style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".4rem 0 0" }}>India vs China sourcing</p>
          </Link>
          <Link to="/trends/container-freight-india-2026" style={{ display: "block", padding: "1.2rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit" }}>
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>Trends & News</p>
            <p style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".4rem 0 0" }}>Container freight 2026</p>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
        <Reveal as="h2" className="display">
          {data.country} sourcing brief — request from Asia Sourcing.
        </Reveal>
        <Reveal as="div" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "2rem" }}>
          <Link
            to={`/contact?category=${encodeURIComponent(data.country)}`}
            className="btn btn-fill btn-lg btn-glow"
            data-testid="region-cta-enquiry"
          >
            Send an enquiry →
          </Link>
          <Link to="/regions" className="btn btn-ghost" data-testid="region-cta-back">
            See all regions
          </Link>
        </Reveal>
      </section>

      {/* PAGINATION */}
      <nav
        className="wrap"
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          paddingBlock: "2rem 4rem",
          borderTop: "1px solid var(--line)",
          fontFamily: "var(--mono)",
          fontSize: ".74rem",
          letterSpacing: ".08em",
          textTransform: "uppercase",
        }}
        aria-label="Region pagination"
        data-testid="region-pagination"
      >
        <Link to={`/regions/${prev.id}`} className="btn btn-ghost">
          ← {prev.country}
        </Link>
        <Link to={`/regions/${next.id}`} className="btn btn-ghost">
          {next.country} →
        </Link>
      </nav>
    </article>
  );
};

export default RegionDetail;
